import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, Wallet, ShieldCheck, KeyRound, Settings, LogOut, Briefcase, Coins, CheckCircle2, Loader2, UserCheck, Tags, Pencil, Trash2, Plus, BellRing, BellOff } from "lucide-react";
import { SoundUpload } from "@/pages/admin/AdminPush";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fileUrl, API } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { fmtDate, rupiah, METHOD_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { PasswordForm } from "@/pages/ProfilePages";
import ImageUpload from "@/components/ImageUpload";
import RegionSelect from "@/components/RegionSelect";

const ADMIN_LIVE = { live: (e) => e.type === "admin" };

const NAV = [
  ["/admin", "Dashboard", LayoutDashboard, "admin-nav-dashboard"],
  ["/admin/users", "Data User", Users, "admin-nav-users"],
  ["/admin/finance", "Keuangan", Wallet, "admin-nav-finance"],
  ["/admin/kyc", "Verifikasi KYC", ShieldCheck, "admin-nav-kyc"],
  ["/admin/categories", "Kategori Pekerjaan", Tags, "admin-nav-categories"],
  ["/admin/push", "Push Notifikasi", BellRing, "admin-nav-push"],
  ["/admin/account", "Pengaturan Akun", KeyRound, "admin-nav-account"],
  ["/admin/settings", "Pengaturan Aplikasi", Settings, "admin-nav-settings"],
];

export function AdminLayout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col md:flex-row">
      <aside className="md:w-64 bg-slate-900 text-slate-300 md:min-h-screen p-4 flex md:flex-col gap-1 overflow-x-auto">
        <div className="hidden md:block px-2 py-3 mb-4"><Logo light /><p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-2">Panel Admin</p></div>
        {NAV.map(([to, l, I, tid]) => (
          <NavLink key={to} to={to} end data-testid={tid} className={({ isActive }) => `shrink-0 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-emerald-600 text-white" : "hover:bg-slate-800"}`}>
            <I className="w-4 h-4" />{l}
          </NavLink>
        ))}
        <button data-testid="admin-logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="shrink-0 md:mt-auto flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-red-500/20 text-red-300"><LogOut className="w-4 h-4" />Keluar</button>
      </aside>
      <main className="flex-1 p-5 md:p-10 overflow-x-auto"><Outlet /></main>
    </div>
  );
}

const Head = ({ t, s }) => <div className="mb-6"><h1 className="font-display text-3xl font-extrabold tracking-tight">{t}</h1>{s && <p className="text-slate-500 text-sm">{s}</p>}</div>;

export function AdminDashboard() {
  const { data } = useFetch("/admin/stats", ADMIN_LIVE);
  const cards = data ? [
    ["Total User", data.users, Users, "users"], ["Total Worker", data.workers, UserCheck, "workers"],
    ["Koin Dihasilkan (Potongan)", data.coins_generated, Coins, "coins"], ["Koin Top Up Masuk", data.coins_topup, Wallet, "topup"],
    ["Pekerjaan Selesai", data.jobs_completed, CheckCircle2, "completed"], ["Pekerjaan Berjalan", data.jobs_running, Briefcase, "running"],
  ] : [];
  return (
    <div data-testid="admin-dashboard">
      <Head t="Dashboard" s="Ringkasan aktivitas aplikasi" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(([l, v, I, k], i) => (
          <div key={k} data-testid={`admin-stat-${k}`} className="bg-white rounded-2xl border p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <I className="w-5 h-5 text-emerald-600" /><p className="text-xs text-slate-500 mt-3">{l}</p>
            <p className="font-mono text-3xl font-bold">{v}</p>
            {(k === "coins" || k === "topup") && <p className="text-xs text-slate-400">{rupiah(v * 1000)}</p>}
          </div>
        ))}
      </div>
      {data && <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
        <div className="bg-amber-50 text-amber-900 rounded-2xl p-4">Top up menunggu: <b>{data.topups_pending}</b></div>
        <div className="bg-blue-50 text-blue-900 rounded-2xl p-4">KYC menunggu: <b>{data.kyc_pending}</b></div>
        <div className="bg-slate-200 text-slate-800 rounded-2xl p-4">Job menunggu penawaran: <b>{data.jobs_open}</b></div>
      </div>}
    </div>
  );
}

const Table = ({ head, children, testid }) => (
  <div className="bg-white rounded-2xl border overflow-x-auto"><table data-testid={testid} className="w-full text-sm">
    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{head.map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
    <tbody className="divide-y">{children}</tbody></table></div>
);

function PushCell({ s, loading }) {
  if (loading) return <span className="text-slate-400">...</span>;
  if (!s) return <span className="inline-flex items-center gap-1 font-bold text-slate-400"><BellOff className="w-3 h-3" />Belum</span>;
  return <span title={s.models.join(", ")} className="inline-flex items-center gap-1 font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full whitespace-nowrap"><BellRing className="w-3 h-3" />Tertaut · {s.models[0]}{s.devices > 1 ? ` +${s.devices - 1}` : ""}</span>;
}

function EditUserDialog({ user, onClose, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) setF({ name: user.name || "", whatsapp: user.whatsapp || "", address: user.address || {} });
  }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}`, { ...f, whatsapp: f.whatsapp || null });
      toast.success("Data user diperbarui");
      onClose(); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-user-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Data User</DialogTitle></DialogHeader>
        {f && <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="text-xs font-semibold text-slate-600">Nama</span>
            <input data-testid="edit-user-name" required minLength={2} className="field mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-600">WhatsApp</span>
            <input data-testid="edit-user-whatsapp" className="field mt-1" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></label>
          <RegionSelect value={f.address} onChange={(address) => setF({ ...f, address })} />
          <button data-testid="edit-user-submit" disabled={busy} className="w-full h-11 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Simpan</button>
        </form>}
      </DialogContent>
    </Dialog>
  );
}

function AddCoinsDialog({ user, onClose, onDone }) {
  const [coins, setCoins] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/coins`, { coins: Number(coins), note: note || null });
      toast.success(`${coins} koin ditambahkan. Saldo ${user.name}: ${data.coins} koin`);
      setCoins(""); setNote(""); onClose(); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-coins-dialog">
        <DialogHeader><DialogTitle>Tambah Koin · {user?.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="add-coins-amount" type="number" min="1" required className="field font-mono" placeholder="Jumlah koin" value={coins} onChange={(e) => setCoins(e.target.value)} />
          <p className="text-xs text-slate-500">= {rupiah((Number(coins) || 0) * 1000)}</p>
          <input data-testid="add-coins-note" className="field" maxLength={120} placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button data-testid="add-coins-submit" disabled={busy} className="w-full h-11 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Tambahkan</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminUsers() {
  const { data, reload } = useFetch("/admin/users", ADMIN_LIVE);
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const { data: push } = useFetch("/admin/push-status", ADMIN_LIVE);
  const toggle = async (u) => {
    try { await api.post(`/admin/users/${u.id}/toggle`); toast.success("Status user diperbarui"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <Head t="Data User" s={`${data?.length || 0} pengguna terdaftar`} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} onDone={reload} />
      <AddCoinsDialog user={adding} onClose={() => setAdding(null)} onDone={reload} />
      <Table head={["Nama", "Email", "Provinsi", "Kabupaten/Kota", "Push HP", "Worker", "KYC", "Koin", "Status", "Aksi"]} testid="admin-user-table">
        {(data || []).map((u) => (
          <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
            <td className="px-4 py-3 font-bold">{u.name}</td><td className="px-4 py-3 text-slate-500">{u.email}</td>
            <td data-testid={`user-province-${u.id}`} className="px-4 py-3 text-xs">{u.address?.province || "-"}</td>
            <td data-testid={`user-regency-${u.id}`} className="px-4 py-3 text-xs">{u.address?.regency || "-"}</td>
            <td data-testid={`user-push-${u.id}`} className="px-4 py-3 text-xs"><PushCell s={push?.[u.id]} loading={!push} /></td>
            <td className="px-4 py-3">{u.is_worker ? <span className="text-emerald-600 font-bold">Aktif</span> : <span className="text-slate-400">-</span>}</td>
            <td className="px-4 py-3 text-xs">{u.kyc_status}</td><td className="px-4 py-3 font-mono">{u.coins}</td>
            <td className="px-4 py-3">{u.is_active ? <span className="text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">Aktif</span> : <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-1 rounded-full">Nonaktif</span>}</td>
            <td className="px-4 py-3 flex gap-2"><button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="text-xs font-bold px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button><button data-testid={`add-coins-${u.id}`} onClick={() => setAdding(u)} className="text-xs font-bold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-1"><Coins className="w-3 h-3" />Tambah Koin</button><button data-testid={`toggle-user-${u.id}`} onClick={() => toggle(u)} className={`text-xs font-bold px-3 py-1.5 rounded-full ${u.is_active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>{u.is_active ? "Nonaktifkan" : "Aktifkan"}</button></td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

const ST = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };
const STL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" };
const Pill = ({ s }) => <span className={`text-xs font-bold px-2 py-1 rounded-full ${ST[s]}`}>{STL[s]}</span>;

function Actions({ onApprove, onReject, id }) {
  return (
    <div className="flex gap-2">
      <button data-testid={`approve-${id}`} onClick={onApprove} className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Setujui</button>
      <button data-testid={`reject-${id}`} onClick={onReject} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100">Tolak</button>
    </div>
  );
}

export function AdminFinance() {
  const tops = useFetch("/admin/topups", ADMIN_LIVE);
  const pays = useFetch("/admin/payouts", ADMIN_LIVE);
  const [tab, setTab] = useState("topup");
  const act = async (url, r) => { try { await api.post(url); toast.success("Berhasil diperbarui"); r(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <Head t="Keuangan" s="Riwayat top up koin & penarikan saldo worker" />
      <div className="inline-flex p-1 bg-white border rounded-full mb-4">
        {[["topup", "Top Up Koin"], ["payout", "Penarikan"]].map(([k, l]) => <button key={k} data-testid={`finance-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm font-bold ${tab === k ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{l}</button>)}
      </div>
      {tab === "topup" ? (
        <Table head={["ID", "User", "Koin", "Nominal", "Metode", "Gateway", "Tanggal", "Status", "Aksi"]} testid="admin-topup-table">
          {(tops.data || []).map((t) => (
            <tr key={t.id}><td className="px-4 py-3 font-mono text-xs">{t.id}</td><td className="px-4 py-3"><p className="font-bold">{t.user_name}</p><p className="text-xs text-slate-400">{t.user_email}</p></td>
              <td className="px-4 py-3 font-mono">{t.coins}</td><td className="px-4 py-3">{rupiah(t.amount_idr)}</td><td className="px-4 py-3">{METHOD_LABEL[t.method]} {t.channel && `· ${t.channel}`}</td>
              <td className="px-4 py-3 text-xs uppercase">{t.gateway}</td><td className="px-4 py-3 text-xs">{fmtDate(t.created_at)}</td><td className="px-4 py-3"><Pill s={t.status} /></td>
              <td className="px-4 py-3">{t.status === "pending" && <Actions id={`topup-${t.id}`} onApprove={() => act(`/admin/topups/${t.id}/approve`, tops.reload)} onReject={() => act(`/admin/topups/${t.id}/reject`, tops.reload)} />}</td></tr>
          ))}
        </Table>
      ) : (
        <Table head={["ID", "Worker", "Koin", "Nominal", "Tujuan", "Tanggal", "Status", "Aksi"]} testid="admin-payout-table">
          {(pays.data || []).map((p) => (
            <tr key={p.id}><td className="px-4 py-3 font-mono text-xs">{p.id}</td><td className="px-4 py-3 font-bold">{p.user_name}</td><td className="px-4 py-3 font-mono">{p.coins}</td><td className="px-4 py-3">{rupiah(p.amount_idr)}</td>
              <td className="px-4 py-3 text-xs">{p.provider} · {p.account_number}<br />{p.account_name}</td><td className="px-4 py-3 text-xs">{fmtDate(p.created_at)}</td><td className="px-4 py-3"><Pill s={p.status} /></td>
              <td className="px-4 py-3">{p.status === "pending" && <Actions id={`payout-${p.id}`} onApprove={() => act(`/admin/payouts/${p.id}/approve`, pays.reload)} onReject={() => act(`/admin/payouts/${p.id}/reject`, pays.reload)} />}</td></tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function AdminKyc() {
  const { data, reload } = useFetch("/admin/kyc", ADMIN_LIVE);
  const act = async (id, a) => { try { await api.post(`/admin/kyc/${id}/${a}`); toast.success("KYC diperbarui"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <Head t="Verifikasi KYC" s="Setujui identitas agar user dapat menjadi worker" />
      <Table head={["User", "NIK", "Nama KTP", "Tgl Lahir", "Dokumen", "Status", "Aksi"]} testid="admin-kyc-table">
        {(data || []).map((u) => (
          <tr key={u.id}><td className="px-4 py-3"><p className="font-bold">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></td>
            <td className="px-4 py-3 font-mono text-xs">{u.kyc?.nik}</td><td className="px-4 py-3">{u.kyc?.full_name}</td><td className="px-4 py-3 text-xs">{u.kyc?.birth_date}</td>
            <td className="px-4 py-3 text-xs space-x-2">{u.kyc?.ktp_photo && <a className="text-emerald-700 font-bold underline" href={fileUrl(u.kyc.ktp_photo)} target="_blank" rel="noreferrer">KTP</a>}{u.kyc?.selfie_photo && <a className="text-emerald-700 font-bold underline" href={fileUrl(u.kyc.selfie_photo)} target="_blank" rel="noreferrer">Selfie</a>}</td>
            <td className="px-4 py-3"><Pill s={u.kyc_status} /></td>
            <td className="px-4 py-3">{u.kyc_status === "pending" && <Actions id={`kyc-${u.id}`} onApprove={() => act(u.id, "approve")} onReject={() => act(u.id, "reject")} />}</td></tr>
        ))}
      </Table>
    </div>
  );
}

export function AdminCategories() {
  const { data, reload } = useFetch("/admin/categories", ADMIN_LIVE);
  const { loadSettings } = useAuth();
  const [name, setName] = useState("");
  const [edit, setEdit] = useState(null);
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); loadSettings(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const add = (e) => { e.preventDefault(); run(() => api.post("/admin/categories", { name }), "Kategori ditambahkan").then((ok) => ok && setName("")); };
  const saveEdit = () => run(() => api.put(`/admin/categories/${edit.id}`, { name: edit.name }), "Kategori diperbarui").then((ok) => ok && setEdit(null));
  const del = (c) => window.confirm(`Hapus kategori "${c.name}"? Kategori juga dihapus dari keahlian worker.`) && run(() => api.delete(`/admin/categories/${c.id}`), "Kategori dihapus");
  return (
    <div className="max-w-3xl">
      <Head t="Kategori Pekerjaan" s="Atur kategori jasa yang tersedia di aplikasi" />
      <form onSubmit={add} className="flex gap-2 mb-4">
        <input data-testid="category-name-input" className="field h-11" placeholder="Nama kategori baru" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        <button data-testid="add-category-btn" className="shrink-0 h-11 px-5 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center gap-1"><Plus className="w-4 h-4" />Tambah</button>
      </form>
      <Table head={["No", "Nama Kategori", "Worker", "Pekerjaan", "Aksi"]} testid="admin-category-table">
        {(data || []).map((c, i) => (
          <tr key={c.id} data-testid={`category-row-${c.id}`}>
            <td className="px-4 py-3 font-mono text-xs">{i + 1}</td><td className="px-4 py-3 font-bold">{c.name}</td>
            <td className="px-4 py-3 font-mono">{c.worker_count}</td><td className="px-4 py-3 font-mono">{c.job_count}</td>
            <td className="px-4 py-3"><div className="flex gap-2">
              <button data-testid={`edit-category-${c.id}`} onClick={() => setEdit({ ...c })} className="text-xs font-bold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>
              <button data-testid={`delete-category-${c.id}`} onClick={() => del(c)} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"><Trash2 className="w-3 h-3" />Hapus</button>
            </div></td>
          </tr>
        ))}
      </Table>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Edit Kategori</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <input data-testid="edit-category-input" className="field" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            <p className="text-xs text-slate-500">Mengubah nama juga memperbarui keahlian worker & pekerjaan terkait.</p>
            <button data-testid="save-category-btn" onClick={saveEdit} className="w-full h-11 rounded-full bg-emerald-600 text-white font-bold">Simpan</button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminAccount() {
  const { user } = useAuth();
  return (
    <div className="max-w-md">
      <Head t="Pengaturan Akun" s={user.email} />
      <div className="bg-white rounded-2xl border p-5"><PasswordForm /></div>
    </div>
  );
}

function SettingsSection({ title, children }) {
  return <div className="space-y-3 pt-4 border-t"><p className="font-display font-bold">{title}</p>{children}</div>;
}

function TextSetting({ k, label, data, setData, area, placeholder }) {
  const props = { "data-testid": `setting-${k}`, value: data[k] || "", placeholder, onChange: (e) => setData({ ...data, [k]: e.target.value }) };
  return (
    <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span>
      {area ? <textarea className="field mt-1 h-20 py-2" {...props} /> : <input className="field mt-1" {...props} />}</label>
  );
}

export function AdminSettings() {
  const { data, setData } = useFetch("/admin/settings");
  const { loadSettings } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const save = async () => {
    setBusy(true);
    try {
      const keys = ["app_name", "logo_path", "favicon_path", "worker_terms", "login_title", "login_subtitle", "login_tagline", "login_hero_path", "marketplace_url", "cs_whatsapp", "cs_email", "cs_hours", "push_icon_path", "push_sound_path"];
      const payload = Object.fromEntries(keys.filter((k) => data[k] != null).map((k) => [k, data[k]]));
      const { data: d } = await api.put("/admin/settings", { ...payload, fee_percent: Number(data.fee_percent ?? 10), first_job_bonus: Number(data.first_job_bonus ?? 10) });
      setData(d);
      await loadSettings();
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl">
      <Head t="Pengaturan Aplikasi" s="Potongan koin, nama, logo & favicon" />
      <div className="bg-white rounded-2xl border p-5 space-y-5">
        <label className="block"><span className="text-xs font-semibold text-slate-600">Nama Aplikasi</span>
          <input data-testid="setting-app-name" className="field mt-1" value={data.app_name} onChange={(e) => setData({ ...data, app_name: e.target.value })} /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Potongan per pekerjaan worker (%)</span>
          <input data-testid="setting-fee-percent" type="number" min="0" max="100" className="field mt-1 font-mono" value={data.fee_percent ?? 10} onChange={(e) => setData({ ...data, fee_percent: e.target.value })} />
          <span className="text-xs text-slate-500">{data.fee_percent ?? 10}% dari nilai koin pekerjaan dipotong saat selesai. Berkurang 1% untuk worker yang lolos verifikasi identitas dan 1% lagi setelah unggah sertifikat pertama.</span></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Bonus pekerjaan pertama worker (koin)</span>
          <input data-testid="setting-first-job-bonus" type="number" min="0" className="field mt-1 font-mono" value={data.first_job_bonus ?? 10} onChange={(e) => setData({ ...data, first_job_bonus: e.target.value })} />
          <span className="text-xs text-slate-500">Diberikan otomatis sekali saat worker menyelesaikan pekerjaan pertamanya. Isi 0 untuk menonaktifkan.</span></label>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Term and Service Worker</span>
          <textarea data-testid="setting-worker-terms" className="field mt-1 h-56 py-2 leading-relaxed" placeholder="Tulis syarat & ketentuan yang harus disetujui user saat mendaftar menjadi worker"
            value={data.worker_terms || ""} onChange={(e) => setData({ ...data, worker_terms: e.target.value })} />
          <span className="text-xs text-slate-500">Ditampilkan saat user mendaftar menjadi worker. Kosongkan untuk memakai teks bawaan.</span></label>
        <SettingsSection title="Notifikasi">
          <div className="grid grid-cols-[140px_1fr] gap-4 items-start">
            <div><p className="text-xs font-semibold text-slate-600 mb-1">Logo Notifikasi</p><ImageUpload testid="upload-push-icon" kind="branding" label="Ikon (persegi)" value={data.push_icon_path} onChange={(p) => setData((d) => ({ ...d, push_icon_path: p }))} /></div>
            <div><p className="text-xs font-semibold text-slate-600 mb-1">Suara Notifikasi Pesan Masuk</p><SoundUpload value={data.push_sound_path} onChange={(p) => setData((d) => ({ ...d, push_sound_path: p }))} />
              <p className="text-[11px] text-slate-500 mt-2">Suara diputar saat notifikasi masuk ketika aplikasi terbuka. Push di luar aplikasi memakai suara bawaan HP/browser.</p></div>
          </div>
        </SettingsSection>
        <SettingsSection title="Tampilan Halaman Login">
          <TextSetting k="login_title" label="Judul (desktop)" data={data} setData={setData} placeholder="Jasa terpercaya, sekali ketuk." />
          <TextSetting k="login_subtitle" label="Deskripsi (desktop)" data={data} setData={setData} area placeholder="Temukan worker terverifikasi di sekitar Anda..." />
          <TextSetting k="login_tagline" label="Tagline (mobile)" data={data} setData={setData} placeholder="Marketplace jasa on-demand. Aman, cepat, terverifikasi." />
          <div><p className="text-xs font-semibold text-slate-600 mb-1">Gambar Latar Login</p><ImageUpload testid="upload-login-hero" kind="branding" label="Unggah Gambar Login" className="h-40" value={data.login_hero_path} onChange={(p) => setData((d) => ({ ...d, login_hero_path: p }))} /></div>
        </SettingsSection>
        <SettingsSection title="Marketplace & Layanan Pelanggan">
          <TextSetting k="marketplace_url" label="Alamat URL Marketplace" data={data} setData={setData} placeholder="https://marketplace.contoh.id" />
          <TextSetting k="cs_whatsapp" label="WhatsApp Layanan Pelanggan" data={data} setData={setData} placeholder="08xxxxxxxxxx" />
          <TextSetting k="cs_email" label="Email Layanan Pelanggan" data={data} setData={setData} placeholder="cs@contoh.id" />
          <TextSetting k="cs_hours" label="Jam Operasional" data={data} setData={setData} placeholder="Senin - Jumat, 08.00 - 17.00 WIB" />
        </SettingsSection>
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-xs font-semibold text-slate-600 mb-1">Logo</p><ImageUpload testid="upload-logo" kind="branding" label="Unggah Logo" value={data.logo_path} onChange={(p) => setData((d) => ({ ...d, logo_path: p }))} /></div>
          <div><p className="text-xs font-semibold text-slate-600 mb-1">Favicon</p><ImageUpload testid="upload-favicon" kind="branding" label="Unggah Favicon" value={data.favicon_path} onChange={(p) => setData((d) => ({ ...d, favicon_path: p }))} /></div>
        </div>
        {data.logo_path && <p className="text-xs text-slate-400">Pratinjau publik: <a className="underline" href={`${API}/public/logo`} target="_blank" rel="noreferrer">logo</a></p>}
        <button data-testid="save-fee-settings-btn" disabled={busy} onClick={save} className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Simpan Pengaturan</button>
      </div>
    </div>
  );
}
